import { useEffect, useRef, useCallback } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { useUIContext } from '../contexts/UIContext';
import { logSecurityEvent, SECURITY_EVENTS } from '../utils/securityLogger';
import logger from '../utils/logger';

/**
 * Session Timeout Hook
 * Automatically signs out users after period of inactivity
 * 
 * Security Benefits:
 * - Prevents unauthorized access from unattended devices
 * - Reduces risk of session hijacking
 * - Compliance with security best practices
 * 
 * @param {number} timeoutMinutes - Minutes of inactivity before logout (default: 30)
 */
export const useSessionTimeout = (timeoutMinutes = 30) => {
  const { user, signOut } = useAuthContext();
  const { showFeedback } = useUIContext();
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  
  // Convert minutes to milliseconds
  const TIMEOUT_MS = timeoutMinutes * 60 * 1000;
  const WARNING_MS = (timeoutMinutes - 2) * 60 * 1000; // Warn 2 minutes before timeout

  const resetTimer = useCallback(() => {
    // Clear existing timers
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }

    // Only set timeout if user is logged in
    if (!user) return;

    // Set warning timer (2 minutes before timeout)
    warningTimeoutRef.current = setTimeout(() => {
      showFeedback(
        'You will be signed out in 2 minutes due to inactivity.',
        'warning',
        5000
      );
    }, WARNING_MS);

    // Set logout timer
    timeoutRef.current = setTimeout(async () => {
      logger.info('SessionTimeout', 'Info', timeoutMinutes, 'minutes of inactivity');
      
      try {
        // Log security event
        await logSecurityEvent(SECURITY_EVENTS.LOGOUT, {
          reason: 'session_timeout',
          inactivityMinutes: timeoutMinutes
        });

        // Sign out user
        await signOut();
        
        showFeedback(
          'You have been signed out due to inactivity.',
          'info',
          5000
        );
      } catch (error) {
        logger.error('SessionTimeout', 'Error', error);
      }
    }, TIMEOUT_MS);
  }, [user, timeoutMinutes, TIMEOUT_MS, WARNING_MS, signOut, showFeedback]);

  // Activity event listeners
  useEffect(() => {
    if (!user) return;

    // Events that indicate user activity
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];

    // Throttle reset to avoid excessive timer resets
    let throttleTimeout;
    const throttledResetTimer = () => {
      if (!throttleTimeout) {
        resetTimer();
        throttleTimeout = setTimeout(() => {
          throttleTimeout = null;
        }, 1000); // Only reset timer once per second at most
      }
    };

    // Add event listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, throttledResetTimer);
    });

    // Initial timer start
    resetTimer();

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, throttledResetTimer);
      });
      
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
    };
  }, [user, resetTimer]);

  // Clear timers when user logs out manually
  useEffect(() => {
    if (!user) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
    }
  }, [user]);
};

export default useSessionTimeout;

